import {
  getData,
  getDataWithError,
  postData,
  postFormData,
  postDataAnonymously,
  putFormData,
} from "./apiCalls";
import { settings as s } from "../Settings";
import { replace } from "react-router-dom";
// login
export const login = async (data) => {
  try {
    const response = await postDataAnonymously(`${s.chatuser.login}`, data);
    return response;
  } catch (err) {
    return null;
  }
};
// RingCentral oauth login
export const ringCentralLogin = async (accessToken) => {
  try {
    const response = await getData(
      `${s.rccall.oauthLogin.replace(`[AccessToken]`, accessToken)}`,
    );
    return response;
  } catch (err) {
    return null;
  }
};
// RingCentral account details
export const ringCentralMe = async (accessToken) => {
  try {
    const response = await getData(
      `${s.rccall.me.replace(`[AccessToken]`, accessToken)}`,
    );
    return response;
  } catch (err) {
    return null;
  }
};
// RingCentral SIP info
export const ringCentralSipInfo = async (appUserId) => {
  try {
    const response = await getData(
      `${s.rccall.sipInfo.replace(`[appUserId]`, appUserId)}`,
    );
    return response;
  } catch (err) {
    return null;
  }
};
// RingCentral outbound extension pool: check out a free extension for this user.
// Returns the axios response (200 with sipInfo) or null on failure / 409 busy.
export const ringCentralAllocateExtension = async (appUserId) => {
  try {
    // Use getDataWithError (not getData) so the backend's {error:"..."} body on a
    // 400 provisioning/JWT failure survives — getData replaces it with [], which
    // erased the real reason and made every failure show the vague "could not
    // connect a calling line" instead of e.g. "RingCentral JWT auth failed: ...".
    const response = await getDataWithError(
      `${s.rccall.allocateExtension.replace(`[appUserId]`, appUserId)}`,
    );
    return response;
  } catch (err) {
    // Return the error RESPONSE (not null) so the caller can tell a true
    // pool-exhausted 409 (poolExhausted:true -> "all lines busy") apart from a
    // provisioning/JWT failure 400 (-> "call failed", with the backend error).
    // Swallowing to null erased that distinction and made every failure look
    // like a busy pool even when the pool was actually free.
    return err?.response || null;
  }
};
// Release the extension held by this user (call on hangup / logout).
export const ringCentralReleaseExtension = async (appUserId) => {
  try {
    return await postData(
      `${s.rccall.releaseExtension.replace(`[appUserId]`, appUserId)}`,
      {},
    );
  } catch (err) {
    return null;
  }
};
// Keep the held extension alive during a call (pool reclaims stale holds).
export const ringCentralExtensionHeartbeat = async (appUserId) => {
  try {
    return await postData(
      `${s.rccall.extensionHeartbeat.replace(`[appUserId]`, appUserId)}`,
      {},
    );
  } catch (err) {
    return null;
  }
};
// RingCentral live call status (real PSTN telephony state: Ringing/CallConnected)
export const ringCentralCallStatus = async (toNumber, appUserId) => {
  try {
    const response = await getData(
      `${s.rccall.callStatus
        .replace(`[toNumber]`, encodeURIComponent(toNumber || ""))
        .replace(`[appUserId]`, encodeURIComponent(appUserId || ""))}`,
    );
    return response;
  } catch (err) {
    return null;
  }
};
// RingCentral: caller number of the current INBOUND call (read from RC active-calls).
// Used for the incoming-call popup since the WebPhone SDK doesn't expose it reliably.
export const ringCentralIncomingCaller = async () => {
  try {
    return await getData(s.rccall.incomingCaller);
  } catch (err) {
    return null;
  }
};
// RingCentral: start call recording (needs the live telephony session + our party id)
export const ringCentralStartRecording = async (
  appUserId,
  telephonySessionId,
  partyId,
) => {
  try {
    const url = s.rccall.startRecording
      .replace(`[appUserId]`, appUserId)
      .replace(`[telephonySessionId]`, encodeURIComponent(telephonySessionId))
      .replace(`[partyId]`, encodeURIComponent(partyId));
    return await postData(url, {});
  } catch (err) {
    return null;
  }
};
// RingCentral: stop call recording -> returns recordingId
export const ringCentralStopRecording = async (
  appUserId,
  telephonySessionId,
  partyId,
  recordingId,
) => {
  try {
    const url = s.rccall.stopRecording
      .replace(`[appUserId]`, appUserId)
      .replace(`[telephonySessionId]`, encodeURIComponent(telephonySessionId))
      .replace(`[partyId]`, encodeURIComponent(partyId))
      .replace(`[recordingId]`, encodeURIComponent(recordingId));
    return await postData(url, {});
  } catch (err) {
    return null;
  }
};
// RingCentral: absolute URL the backend streams the recording file from (proxied,
// since the RC contentUri needs a token the browser doesn't have).
export const ringCentralRecordingContentUrl = (recordingId) =>
  `${s.baseUrl}${s.rccall.recordingContent.replace(
    `[recordingId]`,
    encodeURIComponent(recordingId),
  )}`;
// RingCentral: list recent recorded calls (recordingId + number + time + duration),
// straight from the RC call log — used to show download buttons in call history.
export const ringCentralRecordingsList = async (perPage = 250) => {
  try {
    return await getData(
      s.rccall.recordingsList.replace(`[perPage]`, perPage),
    );
  } catch (err) {
    return null;
  }
};
// RingCentral: look up a finished call's AUTOMATIC recording id by telephony session.
export const ringCentralSessionRecording = async (telephonySessionId) => {
  try {
    const url = s.rccall.sessionRecording.replace(
      `[telephonySessionId]`,
      encodeURIComponent(telephonySessionId),
    );
    return await getData(url);
  } catch (err) {
    return null;
  }
};
// System-wide calling provider (RingCentral | Twilio) — stored in DB, shared by
// all users. Returns { success, provider }.
export const getCallProvider = async () => {
  try {
    return await getData(s.twilio.getCallProvider);
  } catch (err) {
    return null;
  }
};
// Change the system-wide calling provider (persists to DB + broadcasts to all
// connected clients via SignalR "CallProviderChanged").
export const setCallProvider = async (provider, userId) => {
  try {
    const url = s.twilio.setCallProvider
      .replace(`[provider]`, encodeURIComponent(provider))
      .replace(`[userId]`, encodeURIComponent(userId ?? ""));
    return await postData(url, {});
  } catch (err) {
    return null;
  }
};
// send otp
export const sendPassOtp = async (email) => {
  try {
    const response = await postDataAnonymously(
      `${s.chatuser.sendOtp.replace(`[email]`, email)}`,
    );
    return response;
  } catch (err) {
    return null;
  }
};
// verify otp
export const verifyOtp = async (email, otp) => {
  try {
    const response = await postDataAnonymously(
      `${s.chatuser.verifyOTP.replace(`[email]`, email).replace(`[otp]`, otp)}`,
    );
    return response;
  } catch (err) {
    return null;
  }
};
// resetPassword
export const resetPassword = async (email, password) => {
  try {
    const response = await postDataAnonymously(
      `${s.chatuser.resetPassword
        .replace(`[email]`, email)
        .replace(`[password]`, password)}`,
    );
    return response;
  } catch (err) {
    return null;
  }
};
// resetPassword
export const refresh_Token = async (refreshToken) => {
  try {
    const response = await postData(
      `${s.chatuser.refreshToken.replace(`[refreshAccessToken]`, refreshToken)}`,
    );
    return response;
  } catch (err) {
    return null;
  }
};
// getData
export const getUsers = async (page, pageSize, username, depId, status) => {
  try {
    const url = `${s.chatuser.getUsers
      .replace(`[page]`, page)
      .replace(`[pageSize]`, pageSize)
      .replace(`[username]`, username)
      .replace(`[depId]`, depId)
      .replace(`[status]`, status)}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    return null;
  }
};
// getUsersList
export const getUsersList = async () => {
  try {
    const url = `${s.chatuser.getUsersList}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    return null;
  }
};
// region getFolders
export const getFolders = async (userId) => {
  try {
    const url = `${s.emails.getUserFolders.replace(`$[UserId]`, userId)}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    return null;
  }
};
// region AddFolders
export const addFolders = async (data) => {
  try {
    const response = await postData(`${s.emails.addUserFolder}`, data);
    return response;
  } catch (err) {
    return null;
  }
};
// region UpdateFolders
export const updateFolders = async (data) => {
  try {
    const response = await postData(`${s.emails.updateUserFolder}`, data);
    return response;
  } catch (error) {
    return null;
  }
};
// region DelTrashEmails
export const delTrashEmails = async (data) => {
  try {
    const response = await postData(`${s.emails.updateEmail}`, data);
    return response;
  } catch (err) {
    return null;
  }
};
// region getEmailList or inbox
export const getEmailList = async (
  userId,
  PageNumber,
  Rows,
  Text,
  Date,
  HasAttachment,
) => {
  try {
    const url = `${s.emails.getEmailsList
      .replace(`$[UserId]`, userId)
      .replace(`$[PageNumber]`, PageNumber)
      .replace(`$[Rows]`, Rows)
      .replace(`$[Text]`, Text)
      .replace(`$[Date]`, Date)
      .replace(`$[HasAttachment]`, HasAttachment)}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    return null;
  }
};

// region getEmailListSent
export const getEmailListSent = async (
  userId,
  PageNumber,
  Rows,
  IsSent,
  Text,
  Date,
  HasAttachment,
) => {
  try {
    const url = `${s.emails.getEmailsListSent
      .replace(`$[UserId]`, userId)
      .replace(`$[PageNumber]`, PageNumber)
      .replace(`$[Rows]`, Rows)
      .replace(`$[IsSent]`, IsSent)
      .replace(`$[Text]`, Text)
      .replace(`$[Date]`, Date)
      .replace(`$[HasAttachment]`, HasAttachment)}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    return null;
  }
};
// region getEmailListDraft
export const getEmailListDraft = async (
  userId,
  PageNumber,
  Rows,
  IsDraft,
  Text,
) => {
  try {
    const url = `${s.emails.getEmailsListDraft
      .replace(`$[UserId]`, userId)
      .replace(`$[PageNumber]`, PageNumber)
      .replace(`$[Rows]`, Rows)
      .replace(`$[IsDraft]`, IsDraft)
      .replace(`$[Text]`, Text)}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    return null;
  }
};
// region getEmailListFav
export const getEmailListFav = async (
  userId,
  PageNumber,
  Rows,
  IsFav,
  Text,
  Date,
  HasAttachment,
) => {
  try {
    const url = `${s.emails.getEmailsListFav
      .replace(`$[UserId]`, userId)
      .replace(`$[PageNumber]`, PageNumber)
      .replace(`$[Rows]`, Rows)
      .replace(`$[IsFav]`, IsFav)
      .replace(`$[Text]`, Text)
      .replace(`$[Date]`, Date)
      .replace(`$[HasAttachment]`, HasAttachment)}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    return null;
  }
};
// region getEmailListTrash
export const getEmailListTrash = async (
  userId,
  PageNumber,
  Rows,
  IsTrash,
  Text,
) => {
  try {
    const url = `${s.emails.getEmailsListTrash
      .replace(`$[UserId]`, userId)
      .replace(`$[PageNumber]`, PageNumber)
      .replace(`$[Rows]`, Rows)
      .replace(`$[IsTrash]`, IsTrash)
      .replace(`$[Text]`, Text)}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    return null;
  }
};
// region getEmailDetailList
export const getEmailDetailList = async (EmailId) => {
  try {
    const url = `${s.emails.getEmailDetail.replace(`$[EmailId]`, EmailId)}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    return null;
  }
};
// region getIndividualFoldersEmailsList
export const getIndividualFoldersEmails = async (
  userId,
  PageNumber,
  Rows,
  foldersId,
  Text,
  Date,
  hasAttachment,
) => {
  try {
    const url = `${s.emails.getIndividualFoldersEmailsList
      .replace(`$[UserId]`, userId)
      .replace(`$[PageNumber]`, PageNumber)
      .replace(`$[Rows]`, Rows)
      .replace(`$[FolderId]`, foldersId)
      .replace(`$[Text]`, Text)
      .replace(`$[Date]`, Date)
      .replace(`$[HasAttachment]`, hasAttachment)}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    return null;
  }
};
// region Update Email
export const updateEmail = async (data) => {
  try {
    const response = await postData(`${s.emails.updateEmail}`, data);
    return response;
  } catch (error) {
    return null;
  }
};

// region Move to folder
export const moveToFolder = async (data) => {
  try {
    const response = await postData(`${s.emails.moveToFolder}`, data);
    return response;
  } catch (error) {
    return null;
  }
};

// sent Email postFormData
export const sentEmailData = async (data) => {
  try {
    const response = await postFormData(`${s.emails.sendEmail}`, data);
    return response;
  } catch (err) {
    return null;
  }
};
// getUsersCSV
export const getUsersCSV = async (page, pageSize, username, depId, status) => {
  try {
    const url = `${s.chatuser.getUsersCSV
      .replace(`[page]`, page)
      .replace(`[pageSize]`, pageSize)
      .replace(`[username]`, username)
      .replace(`[depId]`, depId)
      .replace(`[status]`, status)}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    return null;
  }
};
// region getUsers Contact Module Data to Excel
export const ExportUserContacts = async (UserId) => {
  try {
    const url = `${s.chatuser.ExportUserContactsToExcel.replace(
      `[UserId]`,
      UserId,
    )}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    return null;
  }
};
//region Send External Msgs
export const sentExternalChatMessages = async (data, tempID) => {
  try {
    const response = await postData(
      `${s.chats.getExternalChats.replace(`[tempID]`, tempID)}`,
      data,
    );
    return response;
  } catch (err) {
    return null;
  }
};
// getDataById
export const getUsersById = async (userId) => {
  try {
    const url = `${s.chatuser.getUserById.replace(`[UserId]`, userId)}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    return null;
  }
};

// getContactById
export const getUserContactsById = async (userId) => {
  try {
    const url = `${s.chatuser.getUserContactsById.replace(`[UserId]`, userId)}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    return null;
  }
};
// getContactsByParams
export const getUserContactsByParams = async (
  userId,
  Rows,
  PageNumber,
  isFav,
  isExternal,
  UserName,
) => {
  try {
    const url = `${s.chatuser.getUserContactsByParams
      .replace(`[UserId]`, userId)
      .replace("[Rows]", Rows)
      .replace("[PageNumber]", PageNumber)
      .replace("[isFav]", isFav)
      .replace("[IsExternal]", isExternal)
      .replace("[UserName]", UserName)}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    return null;
  }
};
export const getAutoForward = async (isExternal) => {
  try {
    const url = `${s.chatuser.getAutoForward.replace(
      "[isExternal]",
      isExternal,
    )}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    return null;
  }
};
// getData Department
export const getDepertmentListData = async () => {
  try {
    const url = `${s.chatuser.getDepertmentList}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    return null;
  }
};
// getData Desination
export const getDesignationListById = async (DepertmentId) => {
  try {
    const url = `${s.chatuser.getDesignationListById.replace(
      `[DepertmentId]`,
      DepertmentId,
    )}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    return null;
  }
};

//get Call Recordings
export const callRecordings = async (autoRecording, sid) => {
  try {
    const url = `${s.chats.getCallRecordings
      .replace(`[autoRecording]`, autoRecording)
      .replace(`$[sid]`, sid)}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    return null;
  }
};
// addData by post method
export const addUser = async (data) => {
  try {
    const response = await postData(`${s.chatuser.addUser}`, data);
    return response;
  } catch (err) {
    return null;
  }
};
// addData by post method
export const updateUserContact = async (data) => {
  try {
    const response = await postData(`${s.chatuser.updateUserContact}`, data);
    return response;
  } catch (err) {
    return null;
  }
};
// Update Data by post method
export const updateUser = async (data) => {
  try {
    const response = await postData(`${s.chatuser.updateUser}`, data);
    return response;
  } catch (err) {
    return null;
  }
};
// add postFormData
export const addUserFormData = async (data) => {
  try {
    const response = await postFormData(`${s.chatuser.addUser}`, data);
    return response;
  } catch (err) {
    return null;
  }
};
// update postformdata
export const updateUserFormData = async (data) => {
  try {
    const response = await postFormData(`${s.chatuser.updateUser}`, data);
    return response;
  } catch (error) {
    return null;
  }
};
// get roles
export const getRolesList = async () => {
  try {
    const url = `${s.chatuser.getRolesList}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    return null;
  }
};

export const addUserContacts = async (data, newexternal = "") => {
  try {
    const response = await postData(
      `${s.chatuser.addUserContacts}`.replace(`$[newexternal]`, newexternal),
      data,
    );
    return response;
  } catch (error) {
    return null;
  }
};

// region addAutoForwarning chat
export const addAutoForwarning = async (chatData, newteam) => {
  try {
    const response = await postData(
      `${s.chats.addAutoForwarning.replace(`$[newteam]`, newteam)}`,
      chatData,
    );
    return response;
  } catch (error) {
    return null;
  }
};
// region add External chat
export const addExternalChat = async (ExternalPersonContactNo) => {
  try {
    const response = await postData(
      `${s.chats.addExternalChat.replace(
        `$[ExternalPersonContactNo]`,
        ExternalPersonContactNo.trim(),
      )}`,
    );
    return response;
  } catch (error) {
    return null;
  }
};

// region update updateAutoForwardTeam
export const updateAutoForwardTeam = async (data) => {
  try {
    const response = await postData(
      `${s.chatuser.updateAutoForwardTeam}`,
      data,
    );
    return response;
  } catch (err) {
    return null;
  }
};

//Chats
export const getUserContactStatusLists = async (userId) => {
  try {
    const url = `${s.chatuser.getUserContactStatusLists}`.replace(
      `$[userId]`,
      userId,
    );
    const response = await getData(url);
    return response;
  } catch (error) {
    return null;
  }
};

//Chats
export const getUserChats = async (
  userId,
  isFav,
  ishide,
  isExternal,
  Autoforward = "",
) => {
  try {
    const url = `${s.chats.getUserChats}`
      .replace(`$[userId]`, userId)
      .replace(`$[isFav]`, isFav)
      .replace(`$[ishide]`, ishide)
      .replace(`$[isExternal]`, isExternal)
      .replace(`$[Autoforward]`, Autoforward);
    const response = await getData(url);
    return response;
  } catch (error) {
    return null;
  }
};
export const getSearchUserChats = async (userId, isFav, IsAdmin) => {
  try {
    const url = `${s.chats.getSearchUserChats}`
      .replace(`$[userId]`, userId)
      .replace(`$[isFav]`, isFav)
      .replace(`$[IsAdmin]`, IsAdmin);
    const response = await getData(url);
    return response;
  } catch (error) {
    return null;
  }
};

export const addChat = async (chatData) => {
  try {
    const response = await postFormData(`${s.chats.addChat}`, chatData);
    return response;
  } catch (error) {
    return null;
  }
};

export const updateChat = async (chatData) => {
  try {
    const response = await postFormData(`${s.chats.updateChat}`, chatData);
    return response;
  } catch (error) {
    return null;
  }
};

export const updateName = async (DeleteName, formData) => {
  try {
    const response = await postFormData(
      `${s.chats.updateNameChat.replace(`$[DeleteName]`, DeleteName)}`,
      formData,
    );
    return response;
  } catch (err) {
    return null;
  }
};

export const updateFavChat = async (favChatData) => {
  try {
    const response = await postData(`${s.chats.updateFavChat}`, favChatData);
    return response;
  } catch (error) {
    return null;
  }
};
// region -- leave group
export const RemoveChatParticipant = async (formData) => {
  try {
    const response = await postData(
      `${s.chats.removeChatParticipant}`,
      formData,
    );
    return response;
  } catch (error) {
    console.error("Error in RemoveChatParticipant:", error);
    return null;
  }
};
// region -- Add Participant Group
export const AddChatParticipant = async (formData) => {
  try {
    const response = await postData(`${s.chats.addChatParticipant}`, formData);
    return response;
  } catch (error) {
    console.error("Error in RemoveChatParticipant:", error);
    return null;
  }
};
export const getChatMessages = async (
  userId,
  chatId,
  isAdmin,
  page,
  pagesize,
) => {
  try {
    const url = `${s.chats.getChatMessages}`
      .replace(`$[userId]`, userId)
      .replace(`$[chatId]`, chatId)
      .replace(`$[isAdmin]`, isAdmin)
      .replace(`$[page]`, page)
      .replace(`$[pagesize]`, pagesize);
    const response = await getData(url);
    return response;
  } catch (error) {
    return null;
  }
};
export const getChatMessagesUser = async (userId, chatId) => {
  try {
    const url = `${s.chats.getChatMessagesUser}`
      .replace(`$[userId]`, userId)
      .replace(`$[chatId]`, chatId);
    const response = await getData(url);
    return response;
  } catch (error) {
    return null;
  }
};

export const getUserContacts = async (userId) => {
  try {
    const url = `${s.chats.getUserContacts}`.replace(`$[userId]`, userId);
    const response = await getData(url);
    return response;
  } catch (error) {
    return null;
  }
};
export const getSearchUserContacts = async (
  userId,
  isAdmin,
  Rows,
  PageNumber,
) => {
  try {
    const url = `${s.chats.getSearchUserContacts}`
      .replace(`$[userId]`, userId)
      .replace(`$[IsAdmin]`, isAdmin)
      .replace(`$[Rows]`, Rows)
      .replace(`$[PageNumber]`, PageNumber);
    const response = await getData(url);
    return response;
  } catch (error) {
    return null;
  }
};

export const addChatMessage = async (chatData, config = {}) => {
  try {
    const response = await postFormData(
      `${s.chats.addChatMessage}`,
      chatData,
      config,
    );
    return response;
  } catch (error) {
    return null;
  }
};

// region -- Attachments
export const GetChatAttachments = async (ChatId) => {
  try {
    const url = `${s.chats?.GetChatAttachments?.replace(`$[ChatId]`, ChatId)}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    return null;
  }
};
//Call History API
export const userCallHistory = async (clientIdentifier, conference) => {
  try {
    const url = `${s.chats?.getCallHistory
      ?.replace(`$[clientIdentifier]`, clientIdentifier)
      .replace(`$[conference]`, conference)}`;
    const response = await getData(url);
    return response;
  } catch (error) {
    console.error("CallHistoryErr", error);
    return null;
  }
};
// region -- ChatPaticcipants
export const getChatParticipants = async (ChatId, UserId) => {
  try {
    const url = `${s.chats?.GetChatParticipants?.replace(
      `$[ChatId]`,
      ChatId,
    ).replace(`$[UserId]`, UserId)}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    console.error("Error fetching chat participants:", err); // Optional: Log the error for debugging
    return null;
  }
};

// Twillio Get Token

export const getTwilioUserToken = async (identity) => {
  try {
    const url = `${s.twilio.getToken}=${identity}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    return null;
  }
};
// Twilio Network Traversal Service — STUN/TURN ICE servers for native WebRTC in-app calls
export const getIceServers = async () => {
  try {
    const response = await getData(`${s.twilio.iceServers}`);
    return response;
  } catch (err) {
    return null;
  }
};
// Twillio Get Video Call Token

export const getVideoCallToken = async (identity, roomName) => {
  try {
    const url = `${s.twilio.getVideoCallToken
      .replace("$[identity]", identity)
      .replace("$[roomName]", roomName)}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    return null;
  }
};

// Add Participant

export const addParticipantPostAPI = async (data) => {
  try {
    const response = await postDataAnonymously(
      `${s.twilio.addParticipant}`,
      data,
    );
    return response;
  } catch (err) {
    return null;
  }
};

// start recording  Call Api
export const startRecordingAPI = async (data) => {
  try {
    const response = await postDataAnonymously(
      `${s.twilio.startRecordingCall}`,
      data,
    );
    return response;
  } catch (err) {
    return null;
  }
};
// stop recording Call Api
export const stopRecordingAPI = async (data) => {
  try {
    const response = await postDataAnonymously(
      `${s.twilio.stopRecordingCall}`,
      data,
    );
    return response;
  } catch (err) {
    return null;
  }
};
// Get Recording Call From API
export const getRecordingCallAPI = async () => {
  try {
    const url = `${s.twilio.getRecordingCall}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    return null;
  }
};
// Fetching Call Details By User Login ID

export const FetchingCallDetailAPI = async (limit) => {
  try {
    // postDataAnonymously
    const response = await getData(`${s.twilio.fetchingCallDetail}`);
    return response;
  } catch (err) {
    return null;
  }
};

// Outbound Call API
export const OutboundCallAPI = async (data) => {
  try {
    const response = await postDataAnonymously(
      `${s.twilio.outboundCall}`,
      data,
    );
    return response;
  } catch (err) {
    return null;
  }
};

// inprogress Call API
export const InProgressCallAPI = async (clientId) => {
  try {
    const response = await postDataAnonymously(
      `${s.twilio.inprogressCall}${clientId}`,
    );
    return response;
  } catch (err) {
    return null;
  }
};
// in Group Call API
export const InGroupCall = async (data) => {
  try {
    const response = await postDataAnonymously(`${s.twilio.groupCall}`, data);
    return response;
  } catch (err) {
    return null;
  }
};

export const fetchCallLogsDetails = async (
  userID,
  page,
  pageSize,
  searchTerm,
) => {
  try {
    let url = `${s.twilio.fetchCallLogsDetails
      .replace(`$[userID]`, userID)
      .replace(`$[page]`, page)
      .replace(`$[pageSize]`, pageSize)
      .replace(`$[searchTerm]`, searchTerm)}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    console.error("Error fetching call logs details:", err);
    return null;
  }
};

export const addCallRecords = async (data) => {
  try {
    const response = await postData(`${s.chatuser.addCallRecords}`, data);
    return response;
  } catch (err) {
    return null;
  }
};
// in Create Room API
export const CreateRoom = async (data) => {
  try {
    const response = await postDataAnonymously(`${s.twilio.createRoom}`, data);
    return response;
  } catch (err) {
    return null;
  }
};

//region Task Management

export const getTasksForBoardView = async (
  taskID,
  userId,
  priority,
  deadline,
  assignedYou,
  assignedByYou,
) => {
  try {
    const response = await getData(
      `${s.tasks.getTasksForBoardView
        .replace(`$[taskID]`, taskID)
        .replace(`$[UserId]`, userId)
        .replace(`$[Priority]`, priority)
        .replace(`$[Deadline]`, deadline)
        .replace(`$[assignedYou]`, assignedYou)
        .replace(`$[assignedByYou]`, assignedByYou)}`,
    );
    return response;
  } catch (err) {
    return null;
  }
};

export const addNewTask = async (data) => {
  try {
    const url = `${s.tasks.addNewTask}`;
    const response = await postFormData(url, data);
    return response;
  } catch (err) {
    return null;
  }
};

export const updateTask = async (data, AssignedYou = "") => {
  try {
    const url = `${s.tasks.updateTask.replace(`$[AssignedYou]`, AssignedYou)}`;
    const response = await postFormData(url, data);
    return response;
  } catch (err) {
    return null;
  }
};

// DataGridViewtable for Tasks
export const getDataGridView = async (
  taskID,
  userId,
  priority,
  deadline,
  assignedYou,
  assignedByYou,
) => {
  try {
    const url = `${s.tasks.GetDataGridTableApi.replace(`$[taskID]`, taskID)
      .replace(`$[UserId]`, userId)
      .replace(`$[Priority]`, priority)
      .replace(`$[Deadline]`, deadline)
      .replace(`$[assignedYou]`, assignedYou)
      .replace(`$[assignedByYou]`, assignedByYou)}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    return null;
  }
};

export const GetDataTableRowTaskId = async (taskId) => {
  try {
    const response = await getData(
      `${s.tasks.GetDataGridTableTaskId.replace(`$[taskID]`, taskId)}`,
    );
    return response;
  } catch (err) {
    return null;
  }
};

export const getTaskActivity = async (taskId) => {
  try {
    const url = `${s.tasks.GetTaskActivity.replace(`$[taskId]`, taskId)}`;
    const response = await getData(url);
    return response;
  } catch (err) {
    console.error("Error fetching task activity:", err);
    return null;
  }
};

export const addTaskActivity = async (taskActivityArray) => {
  try {
    const url = s.tasks.AddTaskActivity;
    const response = await postFormData(url, taskActivityArray);
    return response;
  } catch (err) {
    console.error("Error adding task activity:", err);
    return null;
  }
};

export const removeComment = async (taskActivityId) => {
  try {
    const url = `${s.tasks.RemoveComment.replace(
      `$[taskActivityId]`,
      taskActivityId,
    )}`;

    const response = await postData(url);
    return response;
  } catch (err) {
    console.error("Error removing comment:", err);
    return null;
  }
};
export const updateComment = async (TaskActivityId, CommentText) => {
  try {
    const url = s.tasks.UpdateComment;
    const body = {
      TaskActivityId: TaskActivityId,
      CommentText: CommentText,
    };
    const response = await postData(url, body);
  } catch (err) {
    console.error("Error updating comment:", err);
    return null;
  }
};

// Record Group Call

export const startGroupRecordingAPI = async (data) => {
  try {
    const response = await postDataAnonymously(
      `${s.twilio.group.startGroupRecording}`,
      data,
    );
    return response;
  } catch (err) {
    return null;
  }
};

export const stopGroupRecordingAPI = async (data) => {
  try {
    const response = await postDataAnonymously(
      `${s.twilio.group.stopGroupRecording}`,
      data,
    );
    return response;
  } catch (err) {
    return null;
  }
};

export const getMissedCallCount = async (userId) => {
  try {
    const response = await getData(
      `${s.chats.getMissedCallCount.replace(`$[userId]`, userId)}`,
    );
    return response;
  } catch (err) {
    return null;
  }
};
// addParticipantByFormData
export const addParticipantByFormData = async (data) => {
  try {
    const response = await postFormData(
      `${s.twilio.addParticipantformData}`,
      data,
    );
    return response;
  } catch (err) {
    return null;
  }
};

// MoveToConference testing

export const MoveToConferencePostAPI = async (data) => {
  try {
    const response = await postDataAnonymously(
      `${s.twilio.MoveToConference}`,
      data,
    );
    return response;
  } catch (err) {
    return null;
  }
};
// Add Participant testing

export const MoveAddParticipantPostAPI = async (data) => {
  try {
    const response = await postDataAnonymously(
      `${s.twilio.AddParticipant}`,
      data,
    );
    return response;
  } catch (err) {
    return null;
  }
};
